import {
  createArticlePaymentUrls,
  createPaymentUrls,
  getCountryCode,
} from "@/misc/common";
import { Configuration, QvicklyCreateCheckout, StripeUiMode } from "@/types";
import { AxiosRequestConfig } from "axios";
import { CreateCheckoutPartial } from "./services";
import { getCountryForTimezone } from "countries-and-timezones";

export interface CreateCheckoutExtended extends CreateCheckoutPartial {
  customerEmail: string;
  InternalReferenceId?: string;
  cancellationCode?: string;
}

interface PaymentInterface {
  createCheckout(checkout: CreateCheckoutExtended): AxiosRequestConfig;
}

class QvicklyPayment implements PaymentInterface {
  private url = "/payment/billmate/v1/checkout";
  private configuration: Configuration;

  constructor(configuration: Configuration) {
    this.configuration = configuration;
  }

  getCountryCode() {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const country = getCountryForTimezone(timezone);

    if (
      country?.id &&
      (country.id === "SE" ||
        country.id === "DK" ||
        country.id === "NO" ||
        country.id === "FI")
    ) {
      return country.id;
    }

    return "";
  }

  createCheckout(checkout: CreateCheckoutExtended): AxiosRequestConfig {
    const _createPaymentUrls = !!(
      checkout.Articles.length > 0 && checkout.Articles[0].ArticleId
    )
      ? createArticlePaymentUrls
      : createPaymentUrls;

    const { checkoutUrl, confirmationUrl } = _createPaymentUrls(
      this.configuration,
      checkout.UiMode || "hosted",
      checkout.Id,
      checkout.customerEmail,
      checkout.cancellationCode
    );

    let payloadData: QvicklyCreateCheckout & { CountryCode: string | null } = {
      Articles: checkout.Articles,
      CompanyId: checkout.CompanyId,
      CheckoutUrl: checkoutUrl.href,
      ConfirmationUrl: confirmationUrl.href,
      InternalReferenceId: String(checkout.Id),
      Language: this.configuration.language || "sv",
      CountryCode: this.getCountryCode() ? this.getCountryCode() : null,
    };

    return {
      url: this.url,
      method: "POST",
      data: payloadData,
    };
  }
}

interface StripeCreateCheckout extends CreateCheckoutExtended {
  UiMode?: StripeUiMode;
  ConfirmationUrl?: string;
}
class StripePayment implements PaymentInterface {
  private url = "/payment/stripe/v1/checkout";
  private uiMode: StripeUiMode = "embedded";
  private configuration: Configuration;

  constructor(configuration: Configuration) {
    this.configuration = configuration;
  }

  createCheckout(checkout: StripeCreateCheckout): AxiosRequestConfig {
    const _createPaymentUrls =
      checkout.Articles.length > 0 && checkout.Articles[0].ArticleId
        ? createArticlePaymentUrls
        : createPaymentUrls;

    const { confirmationUrl } = _createPaymentUrls(
      this.configuration,
      // Because it's always embedded
      "embedded",
      checkout.Id,
      checkout.customerEmail,
      checkout.cancellationCode
    );

    // @ts-ignore
    let payloadData: StripeCreateCheckout = {
      Articles: checkout.Articles,
      InternalReferenceId: String(checkout.Id),
      CompanyId: checkout.CompanyId,
      ConfirmationUrl: confirmationUrl.href,
      CountryCode:
        checkout.CountryCode ||
        getCountryCode(this.configuration.language || "sv"),
      UiMode: checkout.UiMode || this.uiMode,
    };

    return {
      url: this.url,
      method: "POST",
      data: payloadData,
    };
  }
}

class Payment implements PaymentInterface {
  private configuration: Configuration;

  constructor(configuration: Configuration) {
    this.configuration = configuration;
  }

  createCheckout(
    checkout: CreateCheckoutExtended | StripeCreateCheckout
  ): AxiosRequestConfig {
    if (checkout.PaymentProviderId === 3) {
      return new QvicklyPayment(this.configuration).createCheckout(checkout);
    } else if (checkout.PaymentProviderId === 5) {
      return new StripePayment(this.configuration).createCheckout(checkout);
    } else {
      throw new Error("Invalid PaymentProviderId");
    }
  }
}

export default Payment;
